import {useCallback, useEffect, useMemo, useState} from 'react';
import {useParams} from 'react-router-dom';
import moment, {Moment} from 'moment';
import {Stack} from '@mui/material';
import SaveIcon from '@mui/icons-material/SaveAlt';
import GenericHeader from '../generic-header/generic-header';
import {translations} from '../../i18n/translations';
import {useTranslation} from 'react-i18next';
import {TaskTable} from '../task-table';
import {EntityUserCareTasksRepository} from '../../repositories/entity-user-care-tasks';
import {CareTaskComplementary} from '../../backend/interfaces/care-task-complementary';
import {UsersRepository} from '../../repositories/users';
import {UserPublicInfo} from '../../backend/responses/user-public-info';
import {useObserveViewport} from '../../hooks';
import {LocalStorageKey, ScreenPaths} from '../../enums';
import {useSeniorSidebar} from '../../contexts/seniors-sidebar/context';
import {DateRangeInput} from '../date-range-input/date-range-input';
import {
  calcEndDateByRange,
  calcStartDateByRange,
  DateRangeType,
} from '../date-range-input/date-range-utils';
import {dateRangeTypes} from '../../screens/private/office/professional-caregiver/occurrences/occurrences.professional-caregiver.screen';
import {UsersFilters} from '../users-filters';
import {createUsersFilters, filterCareTaskInfoByStatus} from '../users-filters/users-filters-utils';
import {createCareTaskInfo} from '../../utils/data/care-task-info';
import {useAuth} from '../../contexts/auth/context';
import {useExportDataWithErrorHandler} from '../../hooks/use-export-data-with-error-handler';
import {
  prepareCareActivitiesPDF,
  prepareCareActivitiesXLSX,
} from '../../utils/data/export-data/care-and-activities/export-data-care-and-activities';
import {exportCareAndActivitiesGetReportData} from '../../utils/data/export-data/care-and-activities/base/export-care-and-activities-get-report-data';

export const CareAndActivity = () => {
  let {entityId, id}: {entityId: string; id: string} = useParams();
  const {t} = useTranslation();
  const sidebarContext = useSeniorSidebar();
  const authContext = useAuth();
  const {previewPdfWithErrorHandling, previewXlsxWithErrorHandling} =
    useExportDataWithErrorHandler();
  const {isRunningMobile} = useObserveViewport();
  const [careTasks, setCareTasks] = useState<Array<CareTaskComplementary> | undefined>(undefined);
  const [rawFilters, setRawFilters] = useState<string>('');
  const [isLoadingTasks, setIsLoadingTasks] = useState(true);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [professionals, setProfessionals] = useState<{[key: string]: UserPublicInfo}>({});
  const [view, setView] = useState<DateRangeType>(
    dateRangeTypes.includes(String(localStorage.getItem(LocalStorageKey.CareAndActivitiesRange)))
      ? (localStorage.getItem(LocalStorageKey.CareAndActivitiesRange) as DateRangeType)
      : '1d'
  );
  const [currentDate, setCurrentDate] = useState<{startDate: Moment; endDate: Moment}>({
    startDate: calcStartDateByRange(view),
    endDate: calcEndDateByRange(view),
  });

  const filterCareTasks = useMemo(() => {
    return fitlerCareTasks(rawFilters, careTasks);
  }, [careTasks, rawFilters]);

  useEffect(() => {
    let requests: Promise<UserPublicInfo>[] = [];
    let requestIds: string[] = [];
    careTasks?.forEach(({careTask}) => {
      const handledByUserId = careTask.markedAsHandledByUserId;

      if (
        handledByUserId &&
        !requestIds.includes(handledByUserId) &&
        !professionals[handledByUserId]
      ) {
        setIsLoadingUsers(true);
        requestIds.push(handledByUserId);
        requests.push(UsersRepository.instance.getInfoById(handledByUserId));
      }
    });
    if (requests.length > 0) {
      Promise.all<Promise<UserPublicInfo>[]>(requests)
        .then((users) => {
          requestIds = requests = [];
          users.forEach((user) => {
            setProfessionals((prev) => {
              if (prev[user.id] === user) return prev;
              return {...prev, [user.id]: user};
            });
          });

          setIsLoadingUsers(false);
        })
        .catch((error) => {
          console.error(error);
          requestIds = requests = [];
          setIsLoadingUsers(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [careTasks, filterCareTasks]);

  const getDayTasks = useCallback(() => {
    if (!entityId || !id) return;
    setIsLoadingTasks(true);

    EntityUserCareTasksRepository.instance
      .GetCareTasksWithComplementaryInformation(
        entityId,
        id,
        currentDate.startDate.toISOString(),
        currentDate.endDate.toISOString()
      )
      .then((response) => {
        setCareTasks(response.data);
      })
      .catch((error) => {
        setCareTasks(undefined);
        console.error(error);
      })
      .finally(() => setIsLoadingTasks(false));
  }, [currentDate, entityId, id]);

  useEffect(() => {
    getDayTasks();
  }, [getDayTasks, currentDate]);

  const senior = useMemo(() => {
    return sidebarContext.state.list?.get(id)?.originalData;
  }, [id, sidebarContext.state.list]);

  return (
    <>
      <GenericHeader
        isLoading={false}
        pageTitle={isRunningMobile ? t(translations.seniorSideMenu.careAndActivity) : undefined}
        titleCrumbs={[
          {
            label: t(translations.screen_paths.professional_caregiver_seniors),
            link: `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_Seniors}`,
          },
          {
            label: senior?.displayName ?? senior?.name ?? '',
            link: `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_Seniors}/${id}${ScreenPaths.Senior_PersonalData}${ScreenPaths.Senior_Profile}`,
          },
          {
            label: t(translations.seniorSideMenu.careAndActivity),
            link: '',
          },
        ]}
        leftColumn={
          <Stack direction="row" justifyContent="flex-end" alignItems="center" gap="10px">
            <UsersFilters
              hideActiveStatus
              hideSocialResponses
              hideOccurrences
              taskStatus={[
                {
                  key: 'planned',
                  label: t(translations.office.users_filters.sections.taskStatus.options.planned),
                },
                {
                  key: 'handled',
                  label: t(translations.office.users_filters.sections.taskStatus.options.handled),
                },
                {
                  key: 'not_handled',
                  label: t(
                    translations.office.users_filters.sections.taskStatus.options.not_handled
                  ),
                },
                {
                  key: 'done',
                  label: t(translations.office.users_filters.sections.taskStatus.options.done),
                },
                {
                  key: 'not_done',
                  label: t(translations.office.users_filters.sections.taskStatus.options.not_done),
                },
              ]}
              onChange={(rawFilters) => {
                setRawFilters(rawFilters);
              }}
            />
          </Stack>
        }
        rightColumn={
          <Stack direction="row" justifyContent="flex-end" alignItems="center" gap="10px">
            <DateRangeInput
              startDate={currentDate.startDate}
              endDate={currentDate.endDate}
              onChange={(startDate, endDate, range) => {
                setCurrentDate({startDate, endDate});
                setView(range);
                localStorage.setItem(LocalStorageKey.CareAndActivitiesRange, range);
              }}
            />
          </Stack>
        }
        actions={{
          actions: [
            {
              label: t(translations.office.generics.save_pdf),
              icon: {element: SaveIcon},
              callback: async () => {
                if (!senior) return;
                const entity = authContext.state.session?.selectedAffiliation?.entity;
                if (!entity) return;

                await previewPdfWithErrorHandling(async () => {
                  const report = await exportCareAndActivitiesGetReportData({
                    entityId,
                    senior,
                    rawFilters,
                    startDate: currentDate.startDate.toISOString(),
                    endDate: currentDate.endDate.toISOString(),
                  });
                  if (!report) {
                    return null;
                  }

                  const pdf = prepareCareActivitiesPDF({
                    reportData: report.reportData,
                    senior,
                    startDate: currentDate.startDate,
                    endDate: currentDate.endDate,
                    entity,
                    t,
                  });

                  return {
                    file: pdf.file,
                    fileName: pdf.fileName,
                  };
                });
              },
            },
            {
              label: t(translations.office.generics.save_xlsx),
              icon: {element: SaveIcon},
              callback: async () => {
                if (!senior) return;

                await previewXlsxWithErrorHandling(async () => {
                  const report = await exportCareAndActivitiesGetReportData({
                    entityId,
                    senior,
                    rawFilters,
                    startDate: currentDate.startDate.toISOString(),
                    endDate: currentDate.endDate.toISOString(),
                  });
                  if (!report) {
                    return null;
                  }

                  const xlsx = prepareCareActivitiesXLSX({
                    reportData: report.reportData,
                    senior,
                    t,
                  });

                  return {
                    file: xlsx.file,
                    fileName: xlsx.fileName,
                  };
                });
              },
            },
          ],
        }}
      />
      <TaskTable
        careTasks={filterCareTasks}
        setCareTasks={setCareTasks}
        isLoading={isLoadingTasks || isLoadingUsers}
        professionals={professionals}
      />
    </>
  );
};

// -------------------------------------------------------------------------------------
// -------------------------------------------------------------------------------------
// -------------------------------------------------------------------------------------

/**
 * Filter the care tasks from `<UsersFilter />` string output.
 */
function fitlerCareTasks(
  rawFilters: string,
  careTasks?: CareTaskComplementary[]
): CareTaskComplementary[] | undefined {
  // ------- No filters -------
  if (!rawFilters) {
    return careTasks;
  }

  // ------- Parse filters -------
  const filters = createUsersFilters(rawFilters);

  // ------- Filter the list -------
  const today = moment();
  let result = careTasks;

  // -- Filter by careTask Status --
  if (filters.taskStatus) {
    result = result?.filter((ctc) => {
      const cti = createCareTaskInfo(ctc, today);
      return filters.taskStatus?.some((status) => filterCareTaskInfoByStatus(cti, status));
    });
  }

  // -- Filter by shifts --
  if (filters.shifts) {
    result = result?.filter((ctc) => {
      return filters.shifts?.some((shift) => ctc.careTask.entityShiftIds?.includes(shift));
    });
  }

  // -- Filter by task activities group sector --
  if (filters.badl || filters.iadl || filters.clinicalSupport || filters.personalDevelopment) {
    result = result?.filter((ctc) => {
      return (
        filters.badl?.some((group) => ctc.careTask.activitiesGroupId?.includes(group)) ||
        filters.iadl?.some((group) => ctc.careTask.activitiesGroupId?.includes(group)) ||
        filters.clinicalSupport?.some((group) => ctc.careTask.activitiesGroupId?.includes(group)) ||
        filters.personalDevelopment?.some((group) =>
          ctc.careTask.activitiesGroupId?.includes(group)
        )
      );
    });
  }

  // -- Done --
  return result;
}
